import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, MessageSquare, Shield, Activity, FileText, Book, GraduationCap } from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();

  const adminLinks = [
    {
      to: '/admin/users',
      icon: Users,
      title: 'User Management',
      description: 'Add, search, and manage member accounts',
    },
    {
      to: '/admin/support',
      icon: MessageSquare,
      title: 'Support Messages',
      description: 'View and respond to support requests',
    },
    {
      to: '/admin/minutes',
      icon: FileText,
      title: 'Meeting Minutes',
      description: 'Add and edit lodge meeting minutes',
    },
    {
      to: '/admin/degree-work',
      icon: Book,
      title: 'Degree Work',
      description: 'Manage ritual and degree materials',
    },
    {
      to: '/admin/learning',
      icon: GraduationCap,
      title: 'Learning Material',
      description: 'Add sections and units to learning modules',
    },
  ];

  return (
    <div className="p-6 md:p-12" data-testid="admin-dashboard">
      <div className="max-w-4xl">
        {/* Welcome Section */}
        <section className="mb-10" data-testid="admin-welcome">
          <div className="flex items-center gap-4 mb-2">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                Admin Console
              </h1>
              <p className="text-gray-600">
                Welcome, {user?.firstName}. Manage lodge members and support requests.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="mb-10" data-testid="admin-stats">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-gray-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">4</p>
                    <p className="text-sm text-gray-500">Total Members</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-gray-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">2</p>
                    <p className="text-sm text-gray-500">Open Support Messages</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-gray-100">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">3</p>
                    <p className="text-sm text-gray-500">Active Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Admin Links */}
        <section data-testid="admin-links">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Administration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {adminLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.to} to={link.to} data-testid={`admin-link-${link.to.split('/').pop()}`}>
                  <Card className="border-gray-100 hover:shadow-md hover:border-amber-200 transition-all h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-amber-700" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-gray-900">{link.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">{link.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
